/*
 * Figma `48:28` — Navigation Item. State: Default, Active, Disabled.
 *
 * Two things it will not do. It never renders `disabled` on the element: a disabled button is
 * skipped by keyboard navigation and announces nothing, so an unreachable destination is
 * `aria-disabled` with an explanation instead — the person can still land on it and be told why.
 * And it keeps its accessible name when the label is hidden: at ≤640px the sidebar is an icon rail
 * with every label display:none, which left every button announcing "button" until TICK-130.
 */
import type { ReactNode } from 'react';

export function NavItem({ icon, label, active = false, disabled = false, badge, reason, onClick, buttonRef }: {
  icon?: ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** A count, e.g. slips waiting. Hidden from the name — the label already says what they are. */
  badge?: number;
  /** Why it cannot be opened. Becomes the title and the announced description when disabled. */
  reason?: string;
  onClick?: () => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="jt-nav-item"
      data-active={active}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      aria-label={label}
      title={disabled && reason ? `${label} — ${reason}` : label}
      onClick={disabled ? undefined : onClick}
    >
      {/*
        * The icon is drawn whether or not one was given. Between 768 and 1279px the sidebar is an
        * icon rail and every label is `display: none` — a destination with no glyph is a button
        * with nothing in it, and twenty of them is a blank column you cannot navigate by sight.
        * Found by opening the console at 1024px rather than by reading this file. The fallback is
        * the label's first character, which is not a good icon but is never an invisible one.
        */}
      <span className="jt-nav-icon" aria-hidden="true">
        {icon ?? <span className="jt-nav-initial">{label.trim().slice(0, 1)}</span>}
      </span>
      <span className="jt-nav-label">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="jt-tab-count">
          {badge}
          <span className="jt-sr-only"> รายการรอดำเนินการ</span>
        </span>
      )}
    </button>
  );
}
